import { api } from '@ppwr/convex/_generated/api'
import { BADGES, isBadgeId, tierName, type BadgeDefinition } from '@ppwr/convex/badges/catalog'
import { useQuery } from 'convex/react'
import { useRouter } from 'expo-router'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useThrottledCallback } from 'use-debounce'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { BadgeArt } from '../../../assets/badges/badgeAssets'
import { SILHOUETTE_PALETTE, TIER_PALETTES, type BadgePalette } from '../../../assets/badges/palettes'

const NAVIGATION_THROTTLE_MS = 1000
const ART_SIZE = 48

interface BadgeColumnProps {
  label: string
  badge: BadgeDefinition
  palette: BadgePalette
  detail: string
}

const BadgeColumn: React.FC<BadgeColumnProps> = ({ label, badge, palette, detail }) => (
  <View style={styles.column}>
    <DS.Text variant="Mini/Bold" color="Brand/Dark Grey" uppercase>
      {label}
    </DS.Text>
    <BadgeArt badge={badge} palette={palette} size={ART_SIZE} />
    <DS.Text variant="Caption/Regular" numberOfLines={1}>
      {badge.name}
    </DS.Text>
    <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
      {detail}
    </DS.Text>
  </View>
)

interface BadgesSummaryCardProps {
  /** Small caption heading rendered inside the card (profile style). Omit for a bare card. */
  title?: string
  /** Analytics event name fired on tap (e.g. events.badges.teaser_tapped). */
  analyticsEvent: string
}

/**
 * Shared most-recent / closest badge summary card, tap → /badges.
 * Used by the Play screen "Your Badges" section and the profile page.
 */
export const BadgesSummaryCard = observer(({ title, analyticsEvent }: BadgesSummaryCardProps) => {
  const router = useRouter()
  const {
    analytics: { setEvent }
  } = useStore()
  const summary = useQuery(api.badges.queries.getBadgeSummary, {})

  const onPress = useThrottledCallback(() => {
    setEvent(analyticsEvent, {
      earnedCount: summary?.earnedCount,
      closestBadgeId: summary?.closest?.badgeId
    })
    router.push('/badges')
  }, NAVIGATION_THROTTLE_MS)

  if (!summary) return null

  const latest = summary.latest && isBadgeId(summary.latest.badgeId) ? summary.latest : null
  const closest = summary.closest && isBadgeId(summary.closest.badgeId) ? summary.closest : null
  const latestBadge = latest ? BADGES[latest.badgeId as keyof typeof BADGES] : null
  const closestBadge = closest ? BADGES[closest.badgeId as keyof typeof BADGES] : null

  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
      <View style={styles.wrapper}>
        <View style={styles.content}>
          {title ? (
            <DS.Text variant="Caption/Bold" uppercase>
              {title}
            </DS.Text>
          ) : null}
          {latestBadge || closestBadge ? (
            <View style={styles.columnsRow}>
              {latestBadge && latest ? (
                <BadgeColumn
                  label="Most Recent"
                  badge={latestBadge}
                  palette={TIER_PALETTES[tierName(latestBadge, latest.tier)]}
                  detail={tierName(latestBadge, latest.tier)}
                />
              ) : null}
              {closestBadge && closest ? (
                <BadgeColumn
                  label="Closest"
                  badge={closestBadge}
                  palette={closest.tier > 0 ? TIER_PALETTES[tierName(closestBadge, closest.tier)] : SILHOUETTE_PALETTE}
                  detail={`${closest.count.toLocaleString()} / ${closest.nextThreshold.toLocaleString()} to ${tierName(closestBadge, closest.tier + 1)}`}
                />
              ) : null}
            </View>
          ) : (
            <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
              Earn badges as you play
            </DS.Text>
          )}
        </View>
        <DS.Icon name="Arrow---right" size={20} color="Global/Black" />
      </View>
    </TouchableOpacity>
  )
})

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    flexDirection: 'row',
    alignItems: 'center',
    padding: DS.spacing[16],
    gap: DS.spacing[12]
  },
  content: {
    flex: 1,
    gap: DS.spacing[12]
  },
  columnsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start'
  },
  column: {
    alignItems: 'center',
    gap: DS.spacing[4],
    maxWidth: '48%'
  }
})